import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Upload as UploadIcon,
  Download as DownloadIcon,
} from "lucide-react";
import { toast } from "react-toastify";
import { imeiAPI } from "../../services";
import { formatImeiDate, generateRecordNumber } from "../../utils/imeiHelpers";
import "../../style/imeiDetail.css";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon.js";
import DeleteModalIcon from "../../assets/delete-modal-icon.svg";
import NoDataIcon from "../../assets/icons/NoDataIcon.js";
import { HiOutlineDuplicate } from "react-icons/hi";
import { ImeiRecordDetailSkeleton } from "../../components/skeleton";
import { useSelector } from "react-redux";

const EMPTY_VALUE = "\u2014";

const RecordSummary = ({ record }) => {
  const recordLabel = record?.id || EMPTY_VALUE;
  const dateValue = record?.created_at
    ? formatImeiDate(record.created_at)
    : EMPTY_VALUE;
  const totalNumbers = Number.isFinite(Number(record?.total_numbers))
    ? Number(record.total_numbers)
    : 0;
  const totalNumbersUploaded = Number.isFinite(
    Number(record?.total_numbers_uploaded)
  )
    ? Number(record.total_numbers_uploaded)
    : 0;

  const summary = [
    { label: "Record ID", value: recordLabel },
    { label: "Created At", value: dateValue },
    { label: "Supplier", value: record?.supplier_name || EMPTY_VALUE },
    { label: "Numbers", value: `${totalNumbers} / ${totalNumbersUploaded}` },
  ];

  const notesCopy =
    typeof record.notes === "string" && record.notes.trim().length > 0
      ? record.notes.trim()
      : EMPTY_VALUE;

  return (
    <div className="detail-summary">
      <div className="summary-grid">
        {summary.map((item) => (
          <div key={item.label} className="summary-item">
            <span className="summary-label">{item.label}</span>
            <span className="summary-value">{item.value}</span>
          </div>
        ))}
      </div>
      <div className="summary-notes">
        <span className="summary-label">Notes</span>
        <p className="summary-notes-value">{notesCopy}</p>
      </div>
    </div>
  );
};

const DeleteImeiModal = ({ open, onCancel, onConfirm, imei, recordLabel }) => {
  if (!open || !imei) return null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deleteImeiTitle"
      >
        <div className="modal-icon">
          <img src={DeleteModalIcon} alt="Delete" />
        </div>
        <h3 className="modal-title" id="deleteImeiTitle">
          Delete IMEI
        </h3>
        <p className="modal-message">
          Are you sure you want to delete IMEI <b>{imei.imei}</b> from record{" "}
          <b>{recordLabel}</b>?<br />
          This action cannot be undone.
        </p>
        <div className="modal-actions">
          <button type="button" className="modal-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="modal-delete-btn"
            onClick={onConfirm}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const DuplicateImeiModal = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div className="duplicate-alert-container">
      <div className="duplicate-alert">
        <div className="warning-icon-wrapper">
          <HiOutlineDuplicate
            className="text-[#FFA20D]"
            style={{ color: "#FFA20D" }}
            size={28}
          />
        </div>

        <h2 className="duplicate-title">Duplicate IMEI</h2>
        <p className="duplicate-message">This IMEI already exists in system</p>

        <button className="duplicate-ok-btn" onClick={onClose}>
          <span>Ok </span>
        </button>
      </div>
    </div>
  );
};
const formatRecordLabel = (index, totalCount) => {
  // Generate descending sequential numbering (newest first)
  // Total count - index gives us descending order
  return String(totalCount - index).padStart(2, '0');
};

const formatImeiValue = (entry) =>
  entry?.imei_number ? entry.imei_number : EMPTY_VALUE;

export default function IMEIRecordDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [input, setInput] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [allNumbers, setAllNumbers] = useState([]);
  const fileInputRef = useRef(null);
  const tableRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const refresh = async (reset = true) => {
    setLoading(true);
    try {
      const response = await imeiAPI.getImeiRecordById(id);

      // Handle the new API response structure
      if (response?.status === "success" && response?.data) {
        setRecord(response.data);
        const imeis = response.data.imeis || [];

        if (reset) {
          setAllNumbers(imeis);
          setNumbers(imeis.slice(0, 50)); // Show first 50 records
          setPage(1);
          setHasMore(imeis.length > 50);
        } else {
          setAllNumbers(imeis);
          setNumbers(imeis.slice(0, 50 * page)); // Show records up to current page
          setHasMore(imeis.length > 50 * page);
        }
      } else {
        // Fallback for old response structure
        const apiData = response?.data || response;
        setRecord(apiData);
        const imeis = apiData?.numbers || apiData?.imeis || [];

        if (reset) {
          setAllNumbers(imeis);
          setNumbers(imeis.slice(0, 50));
          setPage(1);
          setHasMore(imeis.length > 50);
        } else {
          setAllNumbers(imeis);
          setNumbers(imeis.slice(0, 50 * page));
          setHasMore(imeis.length > 50 * page);
        }
      }
    } catch (error) {
      console.error("Error loading IMEI record:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to load IMEI record";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, []);

  const loadMore = () => {
    if (hasMore && !loading) {
      setPage((prevPage) => {
        const newPage = prevPage + 1;
        const newNumbers = allNumbers.slice(0, 50 * newPage);
        setNumbers(newNumbers);
        setHasMore(allNumbers.length > 50 * newPage);
        return newPage;
      });
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loading) {
      loadMore();
    }
  };

  const handleAdd = async () => {
    const value = input.trim();
    if (!value) return;
    try {
      const res = await imeiAPI.addImeiToRecord(id, { imei_number: value });

      // Show success/error toast from backend response
      if (res.status === "success") {
        toast.success(res.message);
        setInput("");
        inputRef.current?.focus();
        refresh(false); // Don't reset pagination
      } else {
        // Check if it's a duplicate IMEI error
        if (
          res.errors?.imei_number?.some(
            (error) =>
              error.toLowerCase().includes("already exists") ||
              error.toLowerCase().includes("duplicate")
          )
        ) {
          setShowDuplicateAlert(true);
        } else {
          // Show specific field errors if available
          if (res.errors?.imei_number && res.errors.imei_number.length > 0) {
            toast.error(res.errors.imei_number[0]);
          } else {
            toast.error(res.message || "Failed to add IMEI");
          }
        }
      }
    } catch (error) {
      console.error("Error adding IMEI:", error);

      // Check if it's a duplicate IMEI error from catch block
      if (
        error.response?.data?.errors?.imei_number?.some(
          (errorMsg) =>
            errorMsg.toLowerCase().includes("already exists") ||
            errorMsg.toLowerCase().includes("duplicate")
        )
      ) {
        setShowDuplicateAlert(true);
      } else {
        // Show specific field errors if available, otherwise show general message
        const backendData = error.response?.data;
        if (backendData?.errors?.imei_number && backendData.errors.imei_number.length > 0) {
          toast.error(backendData.errors.imei_number[0]);
        } else {
          const errorMessage =
            backendData?.message ||
            backendData?.error ||
            "Failed to add IMEI";
          toast.error(errorMessage);
        }
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    // Clear existing timeout
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    // Auto-call API when input reaches 25 characters (typical for complete barcode scan)
    if (value.trim().length >= 25) {
      handleAdd();
    } else if (value.trim()) {
      // Set timeout for shorter inputs that might be scanner input
      scanTimeoutRef.current = setTimeout(() => {
        // Only auto-add if input is 10+ characters (minimum for IMEI/serial)
        if (value.trim().length >= 20) {
          handleAdd();
        }
      }, 200); // 200ms delay for shorter inputs
    }
  };

  const handleDelete = async (imeiId) => {
    try {
      const result = await imeiAPI.deleteImeiFromRecord(imeiId);

      // Show success/error toast from backend response
      if (result.status === "success") {
        toast.success(result.message);
        refresh(false); // Don't reset pagination
      } else {
        toast.error(result.message || "Failed to delete IMEI");
      }
    } catch (error) {
      console.error("Error deleting IMEI:", error);

      // Show error toast from backend response
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete IMEI";
      toast.error(errorMessage);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type - allow Excel and CSV files
    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
      "text/csv", // .csv
      "application/csv", // .csv
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select an Excel (.xlsx, .xls) or CSV file");
      event.target.value = "";
      return;
    }

    // Validate file extension as additional check
    const fileName = file.name.toLowerCase();
    if (
      !fileName.endsWith(".xlsx") &&
      !fileName.endsWith(".xls") &&
      !fileName.endsWith(".csv")
    ) {
      toast.error("Please select an Excel (.xlsx, .xls) or CSV file");
      event.target.value = "";
      return;
    }

    try {
      const result = await imeiAPI.importBulkImei(id, file);

      // Show success/error toast from backend response
      if (result.status === "success") {
        toast.success(result.message);
      } else {
        toast.error(result.message || "Import failed");
      }

      refresh(false); // Don't reset pagination
    } catch (error) {
      console.error("Error importing IMEIs:", error);

      // Show error toast from backend response
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to import file, please try again.";
      toast.error(errorMessage);
    } finally {
      event.target.value = "";
    }
  };

  const handleExport = async () => {
    try {
      const blob = await imeiAPI.exportImeiRecord(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `record_${generateRecordNumber(
        record?.record_no ?? id
      )}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV export ready");
    } catch (error) {
      console.error("Error exporting IMEI record:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Export failed. Please try again.";
      toast.error(errorMessage);
    }
  };

  const rows = useMemo(() => {
    const result = [];
    for (let index = 0; index < numbers.length; index += 2) {
      result.push({
        left: numbers[index] ?? null,
        right: numbers[index + 1] ?? null,
        leftIndex: index,
        rightIndex: index + 1,
      });
    }
    return result;
  }, [numbers]);

  const openDeleteModal = (entry, label) => {
    setDeleteTarget({ entry, label });
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.entry) return;
    await handleDelete(deleteTarget.entry.id);
    closeDeleteModal();
    inputRef.current?.focus();
  };

  if (loading) {
    return <ImeiRecordDetailSkeleton />;
  }

  if (!record) return null;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="imei-detail-page">
        <button
          type="button"
          className={`back${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`}
          onClick={() => navigate("/imei")}
        >
          <ArrowLeft size={16} /> Back to IMEI/Serial Bank
        </button>

        <div className="card">
          <RecordSummary record={record} />

          <div className="entry-toolbar">
            {/* add single IMEI */}
            <div className={`entry-search${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`}>
              <input
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                placeholder="Enter IMEI or serial number..."
                aria-label="IMEI or serial number"
                autoFocus
              />
            </div>
            <button
              type="button"
              className={`btn-outline${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`}
              disabled={!input.trim()}
              onClick={handleAdd}
            >
              Add
            </button>

            <div className="toolbar-actions">
              {/* Import as Bulk */}
              <button
                type="button"
                className={`btn-primary${isPartsURL ? "-parts" : isAccessoriesURL ? "-acc" : ""}`}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon size={16} /> Import as Bulk
              </button>

              {/* IMEI Record Export */}
              <button
                type="button"
                className="btn-ghost"
                onClick={handleExport}
              >
                <DownloadIcon size={16} /> Export
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={handleImport}
            />
          </div>

          <div
            className="imei-table-wrapper"
            ref={tableRef}
            onScroll={handleScroll}
            style={{ maxHeight: "600px", overflowY: "auto" }}
          >
            <table className="imei-table-dual">
              <colgroup>
                <col className="col-record" />
                <col className="col-imei" />
                <col className="col-action" />
                <col className="col-record" />
                <col className="col-imei" />
                <col className="col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th>Record ID</th>
                  <th>IMEI Number</th>
                  <th>Action</th>
                  <th>Record ID</th>
                  <th>IMEI Number</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-imei-message">
                      <div
                        className=""
                        style={{
                          marginBottom: "4rem",
                          marginTop: "4rem",
                          textAlign: "center",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <NoDataIcon />
                        <h3
                          style={{
                            marginTop: "1rem",
                            color: "#6E7781",
                            fontSize: "14px",
                            fontWeight: "400",
                          }}
                        >
                          No IMEI records found
                        </h3>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => {
                    const leftLabel = row.left ? formatRecordLabel(row.leftIndex, allNumbers.length) : EMPTY_VALUE;
                    const rightLabel = row.right ? formatRecordLabel(row.rightIndex, allNumbers.length) : EMPTY_VALUE;

                    return (
                      <tr
                        key={`${row.left?.id || "left-empty"}-${
                          row.right?.id || "right-empty"
                        }-${index}`}
                      >
                        <td data-title="Record ID">{leftLabel}</td>
                        <td data-title="IMEI Number">
                          {formatImeiValue(row.left)}
                        </td>
                        <td className="actions">
                          {row.left ? (
                            <button
                              type="button"
                              title="Delete"
                              className="delete"
                              onClick={() =>
                                row.left && openDeleteModal(row.left, leftLabel)
                              }
                              aria-label="Delete IMEI"
                              disabled={!row.left}
                            >
                              <RedDeleteIcon />
                            </button>
                          ) : (
                            <span className="btn-delete-placeholder">—</span>
                          )}
                        </td>
                        <td data-title="Record ID">{rightLabel}</td>
                        <td data-title="IMEI Number">
                          {formatImeiValue(row.right)}
                        </td>
                        <td className="actions">
                          {row.right ? (
                            <button
                              type="button"
                              title="Delete"
                              className="delete"
                              onClick={() =>
                                row.right &&
                                openDeleteModal(row.right, rightLabel)
                              }
                              aria-label="Delete IMEI"
                              disabled={!row.right}
                            >
                              <RedDeleteIcon />
                            </button>
                          ) : (
                            <span className="btn-delete-placeholder">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Loading indicator and Load More button */}
            {hasMore && (
              <div
                className="load-more-container"
                style={{
                  padding: "20px",
                  textAlign: "center",
                  borderTop: "1px solid #eaeef2",
                }}
              >
                {loading ? (
                  <div className="loading-spinner">
                    <div
                      style={{
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        border: "3px solid #f3f3f3",
                        borderTop: "3px solid #007bff",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    ></div>
                    <span style={{ marginLeft: "10px" }}>
                      Loading more records...
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={loadMore}
                    style={{ padding: "8px 16px" }}
                  >
                    Load More Records
                  </button>
                )}
              </div>
            )}

            {/* Show total count when all records are loaded */}
            {!hasMore && allNumbers.length > 50 && (
              <div
                className="all-loaded-indicator"
                style={{
                  padding: "20px",
                  textAlign: "center",
                  borderTop: "1px solid #eaeef2",
                  color: "#6c757d",
                  fontSize: "14px",
                }}
              >
                All {allNumbers.length} records loaded
              </div>
            )}
          </div>
        </div>

        <DeleteImeiModal
          open={Boolean(deleteTarget)}
          onCancel={closeDeleteModal}
          onConfirm={confirmDelete}
          imei={deleteTarget?.entry}
          recordLabel={deleteTarget?.label}
        />
      </div>

      <DuplicateImeiModal
        open={showDuplicateAlert}
        onClose={() => setShowDuplicateAlert(false)}
      />
    </>
  );
}
